import { deleteInformationApi, useInformationList } from "@/api/services/admin/information";
import { TGetInformationListDto } from "@/api/services/admin/information/dtos/get-information-list.dto";
import { useEnum } from "@/api/services/global/enum";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { Button } from "@/components/elements/Button";
import { Table } from "@/components/elements/Table";
import { DATE_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { useDialog } from "@/hooks/useDialog";
import { useInformationListSessionStorage } from "@/hooks/useInformationListSessionStorage";
import { useSelectionTable } from "@/hooks/useSelectionTable";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { findLabelByValue } from "@/utils/object";
import { getDynamicRoute } from "@/utils/url";
import { useQueryClient } from "@tanstack/react-query";
import { MRT_ColumnDef } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Stack } from "@mui/material";
import { styled } from "@mui/system";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { truncateWithEllipsis } from "@/utils/string";
import { MAX_LENGTH_TITLE } from "@/features/information/constants/informationList";
import { NewPagination } from "@/components/elements/Pagination/NewPagination";

export const InformationTable = () => {
  const { data: informationTypes } = useEnum("EInformationType");
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmDelete = useDialog();
  const { search } = useLocation();
  const { getState, saveState } = useInformationListSessionStorage();

  const defaultSelectionKeys = useMemo(() => {
    const state = getState();
    return state?.selectedKeys ?? [];
  }, []);

  const [heightTable] = useState<string>("calc(100vh - 330px)");

  const handleDelete = async () => {
    try {
      const agreeToDelete = await confirmDelete.show();
      if (!agreeToDelete) return;
      const ids = rowSelectionKeys.map((key) => JSON.parse(key));
      const rs = await deleteInformationApi(ids);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INFORMATION.FETCH_LIST] });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      clearSelection();
    } catch (error) {
      showError(error);
    }
  };

  const isPublished = (row: TGetInformationListDto): boolean => {
    if (!row.startDate) {
      return true;
    }

    const today = new Date().toISOString().split("T")[0];
    if (row.startDate <= today) {
      return true;
    }
    return false;
  };

  const columns = useMemo<MRT_ColumnDef<TGetInformationListDto>[]>(
    () => [
      {
        header: "ステータス",
        size: 60,
        Cell: ({ row }) => {
          const { original } = row;
          if (original.isDraft) {
            return "下書き";
          }

          if (isPublished(original)) {
            return "公開済み";
          }
          return "未公開";
        },
      },
      {
        header: "タイトル",
        Cell: ({ row }) => {
          const { original } = row;
          if (original.title) {
            return truncateWithEllipsis(original.title, MAX_LENGTH_TITLE);
          }
          return EMPTY_STR.TEXT;
        },
      },
      {
        header: "タグ",
        size: 120,
        Cell: ({ row }) => {
          return row.original.type ? findLabelByValue(informationTypes, row.original.type) : EMPTY_STR.TEXT;
        },
      },
      {
        header: "配信開始日",
        size: 120,
        Cell: ({ row }) => {
          const { startDate } = row.original;
          return startDate ? dayjs(startDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
      },
      {
        header: "配信終了日",
        size: 120,
        Cell: ({ row }) => {
          const { endDate } = row.original;
          return endDate ? dayjs(endDate).format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
      },
      {
        header: "",
        id: "edit",
        Cell: ({ row }) => {
          const { id } = row.original;
          return (
            <Button size="sm-round" variant="outline" component={Link} to={getDynamicRoute(AppRoutes.informationEdit, { id })}>
              編集
            </Button>
          );
        },
        grow: false,
        size: 50,
      },
    ],
    [informationTypes],
  );

  const { data, isLoading } = useInformationList();
  const { table, rowSelectionKeys, clearSelection } = useSelectionTable({
    columns,
    rows: data?.data ?? [],
    primaryKey: "id",
    defaultSelectionKeys,
    renderEmptyRowsFallback: ({}) => <span></span>,
  });
  const isDisableButton = rowSelectionKeys.length === 0;

  useEffect(() => {
    saveState({
      searchParams: search.startsWith("?") ? search.slice(1) : search,
      selectedKeys: rowSelectionKeys,
    });
  }, [rowSelectionKeys, search, saveState]);

  return (
    <>
      <Table table={table} isLoading={isLoading} height={heightTable} minHeight={150} />
      <ButtonStyle>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={2}>
          <NewPagination totalPage={data?.pagination.totalPage} />
          <Button disabled={isDisableButton} variant="outline" size="md" onClick={handleDelete}>
            選択した項目を削除
          </Button>
        </Stack>
      </ButtonStyle>
      <ConfirmDeleteModal
        open={confirmDelete.open}
        onClose={confirmDelete.cancel}
        onOk={confirmDelete.confirm}
        message="お知らせを削除してもよろしいですか？"
      />
    </>
  );
};

const ButtonStyle = styled(Stack)`
  width: 100%;
  padding-top: 10px;
  gap: 8px;
`;
